import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { FileMetadata, UploadProgress, UploadStatus } from '../types/file.types';

interface FileState {
  documents: FileMetadata[];
  uploads: Map<string, UploadProgress>;
  isLoading: boolean;
  error: string | null;
  totalDocuments: number;
  currentPage: number;
}

interface FileActions {
  setDocuments: (documents: FileMetadata[], total: number) => void;
  addDocument: (document: FileMetadata) => void;
  removeDocument: (documentId: string) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setPage: (page: number) => void;

  // Upload tracking
  startUpload: (fileId: string, fileName: string) => void;
  updateUploadProgress: (fileId: string, progress: number) => void;
  completeUpload: (fileId: string) => void;
  failUpload: (fileId: string, error: string) => void;
  clearUpload: (fileId: string) => void;
  clearAllUploads: () => void;
}

type FileStore = FileState & FileActions;

export const useFileStore = create<FileStore>()(
  devtools(
    immer((set) => ({
      // Initial state
      documents: [],
      uploads: new Map(),
      isLoading: false,
      error: null,
      totalDocuments: 0,
      currentPage: 1,

      // Document actions
      setDocuments: (documents, total) =>
        set((state) => {
          state.documents = documents;
          state.totalDocuments = total;
          state.isLoading = false;
          state.error = null;
        }),

      addDocument: (document) =>
        set((state) => {
          state.documents.unshift(document);
          state.totalDocuments += 1;
        }),

      removeDocument: (documentId) =>
        set((state) => {
          state.documents = state.documents.filter((doc) => doc.id !== documentId);
          state.totalDocuments -= 1;
        }),

      setLoading: (isLoading) =>
        set((state) => {
          state.isLoading = isLoading;
        }),

      setError: (error) =>
        set((state) => {
          state.error = error;
          state.isLoading = false;
        }),

      setPage: (page) =>
        set((state) => {
          state.currentPage = page;
        }),

      // Upload tracking actions
      startUpload: (fileId, fileName) =>
        set((state) => {
          state.uploads.set(fileId, {
            fileId,
            fileName,
            progress: 0,
            status: UploadStatus.UPLOADING,
          });
        }),

      updateUploadProgress: (fileId, progress) =>
        set((state) => {
          const upload = state.uploads.get(fileId);
          if (upload) {
            upload.progress = progress;
          }
        }),

      completeUpload: (fileId) =>
        set((state) => {
          const upload = state.uploads.get(fileId);
          if (upload) {
            upload.progress = 100;
            upload.status = UploadStatus.COMPLETED;
          }
        }),

      failUpload: (fileId, error) =>
        set((state) => {
          const upload = state.uploads.get(fileId);
          if (upload) {
            upload.status = UploadStatus.FAILED;
            upload.error = error;
          }
        }),

      clearUpload: (fileId) =>
        set((state) => {
          state.uploads.delete(fileId);
        }),

      clearAllUploads: () =>
        set((state) => {
          state.uploads.clear();
        }),
    })),
    { name: 'FileStore' }
  )
);
